import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, App, Button, Col, Form, Input, Row, Switch } from 'antd';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel } from '../../components';
import { useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';

const previewUrl = process.env.REACT_APP_KIVONEX_WEBSITE || (process.env.NODE_ENV === 'development' ? 'http://localhost:5174/' : 'https://kivonex.in/');

const Field = ({ name, label, placeholder, ...props }) => (
  <Form.Item name={name} label={label} {...props}>
    <Input placeholder={placeholder || `Enter ${label.toLowerCase()}`} />
  </Form.Item>
);

/** Settings › Website: public brand, homepage copy, calls to action and visual theme. */
export function WebsiteSettings() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { data, error, loading, reload } = useLoad(() => api('/v1/admin/settings/website').then((body) => body.settings));

  useEffect(() => {
    if (data) form.setFieldsValue(data);
  }, [data, form]);

  async function save(values) {
    setSaving(true);
    try {
      await api('/v1/admin/settings/website', { method: 'PUT', body: JSON.stringify({ ...data, ...values }) });
      message.success('Website content and design saved.');
      await reload();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Could not save the website');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Website"
        description="Manage the public brand, homepage messaging, calls to action and colour system. Changes are read by the website automatically."
        actions={<><Button href={previewUrl} target="_blank">Open website</Button><BackToSettings /></>}
      />
      <ErrorNotice error={error} />
      {loading && !data ? <FormSkeleton sections={5} /> : (
        <Form form={form} layout="vertical" onFinish={save} requiredMark="optional">
          <Alert type="info" showIcon style={{ marginBottom: 20 }} message="Keep claims factual. Saved changes appear after the website’s short public cache expires (up to five minutes)." />

          <Panel title="Brand" description="The product name and short line used across the public site.">
            <Row gutter={16}>
              <Col xs={24} md={8}><Field name={['brand', 'name']} label="Brand name" rules={[{ required: true }]} /></Col>
              <Col xs={24} md={8}><Field name={['brand', 'descriptor']} label="Product descriptor" /></Col>
              <Col xs={24} md={8}><Field name={['brand', 'tagline']} label="Brand line" /></Col>
            </Row>
          </Panel>

          <Panel title="Visual theme" description="Use accessible, high-contrast colours. The website applies them as design tokens everywhere.">
            <Row gutter={16}>
              {[
                ['primary', 'Primary blue'], ['accent', 'Accent'], ['dark', 'Dark surface'], ['surface', 'Light surface']
              ].map(([key, label]) => (
                <Col xs={12} md={6} key={key}>
                  <Form.Item name={['theme', key]} label={label} rules={[{ required: true }, { pattern: /^#[0-9a-f]{6}$/i, message: 'Use #RRGGBB' }]}>
                    <Input type="color" className="website-colour" />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Panel>

          <Panel title="Announcement" description="A compact message above the homepage headline.">
            <Row gutter={16} align="bottom">
              <Col xs={24} md={4}><Form.Item name={['home', 'announcement', 'enabled']} label="Show"><Switch /></Form.Item></Col>
              <Col xs={24} md={10}><Field name={['home', 'announcement', 'text']} label="Message" /></Col>
              <Col xs={12} md={5}><Field name={['home', 'announcement', 'linkLabel']} label="Link label" /></Col>
              <Col xs={12} md={5}><Field name={['home', 'announcement', 'linkUrl']} label="Link" /></Col>
            </Row>
          </Panel>

          <Panel title="Homepage hero" description="The first promise visitors see. Keep the title direct and the description concrete.">
            <Field name={['home', 'hero', 'eyebrow']} label="Eyebrow" />
            <Row gutter={16}>
              <Col xs={24} md={12}><Field name={['home', 'hero', 'title']} label="Headline" rules={[{ required: true }]} /></Col>
              <Col xs={24} md={12}><Field name={['home', 'hero', 'highlight']} label="Highlighted headline" /></Col>
            </Row>
            <Form.Item name={['home', 'hero', 'description']} label="Description" rules={[{ required: true }]}><Input.TextArea rows={3} placeholder="Describe the product’s practical value in one short paragraph" /></Form.Item>
            <Row gutter={16}>
              <Col xs={12} md={6}><Field name={['home', 'hero', 'primaryLabel']} label="Primary button" /></Col>
              <Col xs={12} md={6}><Field name={['home', 'hero', 'primaryUrl']} label="Primary link" /></Col>
              <Col xs={12} md={6}><Field name={['home', 'hero', 'secondaryLabel']} label="Secondary button" /></Col>
              <Col xs={12} md={6}><Field name={['home', 'hero', 'secondaryUrl']} label="Secondary link" /></Col>
            </Row>
          </Panel>

          <Panel title="Proof points" description="Short, verifiable facts shown immediately below the hero.">
            <Form.List name={['home', 'proof']}>
              {(fields, { add, remove }) => <div className="website-proof-fields">
                {fields.map((field) => <div className="website-proof-row" key={field.key}>
                  <Form.Item name={[field.name, 'value']} label="Value" rules={[{ required: true }]}><Input placeholder="14" /></Form.Item>
                  <Form.Item name={[field.name, 'label']} label="Meaning" rules={[{ required: true }]}><Input placeholder="bank statement formats" /></Form.Item>
                  <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} aria-label="Remove proof point" />
                </div>)}
                {fields.length < 6 && <Button icon={<PlusOutlined />} onClick={() => add({ value: '', label: '' })}>Add proof point</Button>}
              </div>}
            </Form.List>
          </Panel>

          <Panel title="Product section" description="The introduction above the connected product modules.">
            <Field name={['home', 'modules', 'eyebrow']} label="Eyebrow" />
            <Field name={['home', 'modules', 'title']} label="Title" rules={[{ required: true }]} />
            <Form.Item name={['home', 'modules', 'description']} label="Description"><Input.TextArea rows={2} placeholder="Explain how the product modules work together" /></Form.Item>
          </Panel>

          <Panel title="Closing call to action" description="The final next step at the bottom of the homepage.">
            <Field name={['home', 'cta', 'eyebrow']} label="Eyebrow" />
            <Field name={['home', 'cta', 'title']} label="Title" rules={[{ required: true }]} />
            <Form.Item name={['home', 'cta', 'description']} label="Description"><Input.TextArea rows={2} placeholder="Give visitors a clear reason to take the next step" /></Form.Item>
            <Row gutter={16}>
              <Col xs={12} md={6}><Field name={['home', 'cta', 'primaryLabel']} label="Primary button" /></Col>
              <Col xs={12} md={6}><Field name={['home', 'cta', 'primaryUrl']} label="Primary link" /></Col>
              <Col xs={12} md={6}><Field name={['home', 'cta', 'secondaryLabel']} label="Secondary button" /></Col>
              <Col xs={12} md={6}><Field name={['home', 'cta', 'secondaryUrl']} label="Secondary link" /></Col>
            </Row>
          </Panel>

          <div className="form-actions website-savebar">
            <Button type="primary" htmlType="submit" loading={saving}>Save website</Button>
            <Button href={previewUrl} target="_blank">Preview current site</Button>
          </div>
        </Form>
      )}
    </>
  );
}
